import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerPerformance = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [athlete, setAthlete] = useState(null);
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchMatches();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      if (response.data.length > 0) {
        const athleteData = response.data[0];
        setAthlete(athleteData);
        fetchPerformances(athleteData.id);
      }
    } catch (error) {
      toast.error('Failed to fetch profile');
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`);
      setMatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch matches');
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformances = async (athleteId) => {
    try {
      const response = await axios.get(`${API}/performance/athlete/${athleteId}`);
      setPerformances(response.data);
    } catch (error) {
      console.error('Failed to fetch performances:', error);
    }
  };

  const getMatchForPerformance = (performance) => {
    return matches.find(m => m.id === performance.match_id);
  };

  const getRatingBadge = (rating) => {
    if (!rating) return null;
    if (rating >= 8) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (rating >= 6) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (rating >= 4) return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Match Performance Reports
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">View your match history and performance feedback</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !athlete ? (
          <Card data-testid="no-athlete-message">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-500">Profile not linked yet. Contact an administrator.</p>
            </CardContent>
          </Card>
        ) : performances.length === 0 ? (
          <Card data-testid="no-performance-message">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-500">No performance reports yet. Reports will appear here after matches.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="performance-list">
            {performances.map((performance) => {
              const match = getMatchForPerformance(performance);
              if (!match) return null;

              return (
                <Card key={performance.id} className="hover:shadow-lg transition-shadow" data-testid={`performance-card-${performance.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl">
                          vs {match.opponent}
                        </CardTitle>
                        <CardDescription>
                          {new Date(match.date).toLocaleDateString()} • {match.competition}
                        </CardDescription>
                        {match.result && (
                          <Badge className="mt-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700">
                            {match.result}
                          </Badge>
                        )}
                      </div>
                      {performance.rating && (
                        <div className="text-center">
                          <Badge className={getRatingBadge(performance.rating)} data-testid={`rating-${performance.id}`}>
                            Rating: {performance.rating}/10
                          </Badge>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {performance.minutes_played && (
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-500">Minutes Played</p>
                          <p className="font-medium" data-testid={`minutes-${performance.id}`}>
                            {performance.minutes_played}&apos;
                          </p>
                        </div>
                      )}
                      {performance.position_played && (
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-500">Position</p>
                          <p className="font-medium">{performance.position_played}</p>
                        </div>
                      )}
                    </div>

                    {performance.coach_comment && (
                      <div className="border-t pt-4">
                        <p className="text-sm text-gray-500 dark:text-gray-500 mb-2">Coach Comment</p>
                        <p className="text-gray-700 dark:text-gray-300" data-testid={`comment-${performance.id}`}>
                          {performance.coach_comment}
                        </p>
                      </div>
                    )}

                    {performance.strengths && (
                      <div className="bg-emerald-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-emerald-900 mb-2">💪 Strengths</p>
                        <p className="text-sm text-emerald-800">{performance.strengths}</p>
                      </div>
                    )}

                    {performance.improvements && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-blue-900 mb-2">📈 Areas for Improvement</p>
                        <p className="text-sm text-blue-800">{performance.improvements}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlayerPerformance;
